import React, { useEffect, useRef, useState } from "react";
import { Leaf, Shield, Users, Wrench, HardHat } from "lucide-react";
import { Container, Row, Col } from 'reactstrap';
import {
  checkSustainability,
  checkEnvironment,
  checkILO,
  checkSafetyp,
  checkSafetyc,
} from '../../api/cof';

// ✅ Circular Progress Component
const CircularProgress = ({
  percentage,
  color,
  size = 100,
  duration = 1000,
}) => {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;

  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const startTimeRef = useRef();
  const animationRef = useRef();

  // Trigger animation only when visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.6 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Animate percentage
  useEffect(() => {
    if (!isVisible) return;

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const progress = timestamp - startTimeRef.current;
      const percent = Math.min((progress / duration) * percentage, percentage);
      setAnimatedPercentage(percent);

      if (progress < duration)
        animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, [isVisible, percentage, duration]);

  const strokeDashoffset =
    circumference - (animatedPercentage / 100) * circumference;

  return (
    <div
      ref={containerRef}
      className="relative flex items-center justify-center"
      style={{ width: size, height: size, margin: 'auto' }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
      >
        <circle
          stroke="#e5e7eb"
          strokeWidth="4"
          fill="transparent"
          r={radius}
          cx="50"
          cy="50"
        />
        <circle
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeWidth="4"
          strokeLinecap="round"
          stroke={color}
          fill="transparent"
          r={radius}
          cx="50"
          cy="50"
          transform="rotate(-90 50 50)"
        />
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dy=".3em"
          className="text-sm font-semibold"
          fill="#374151"
        >
          {Math.round(animatedPercentage)}%
        </text>
      </svg>
    </div>
  );
};

// ✅ Metric Card
const MetricCard = ({ title, percentage, icon: Icon, color, description }) => {
  const getStatusBadge = (p) =>
    p >= 80
      ? 'badge bg-success mb-3'
      : p >= 60
      ? 'badge bg-warning text-dark mb-3'
      : 'badge bg-danger mb-3';

  const getStatusText = (p) =>
    p >= 80 ? 'Excellent' : p >= 60 ? 'Good' : 'Needs Attention';

  const getIconColor = (color) => {
    const map = {
      green: '#22c55e',
      blue: '#3b82f6',
      purple: '#9333ea',
      orange: '#f97316',
      red: '#ef4444',
    };
    return map[color] || '#6b7280';
  };

  return (
    <div className="h-100 w-100">
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 h-100 p-3 d-flex flex-column align-items-center text-center">
        {/* Icon */}
        <div
          className="p-3 mb-3 rounded-circle bg-light d-inline-flex align-items-center justify-content-center"
          style={{ width: '52px', height: '52px' }}
        >
          <Icon
            size={26}
            color={getIconColor(color)}
          />
        </div>

        {/* Title */}
        <h6 className="text-lg font-semibold text-gray-800 mb-2">{title}</h6>

        {/* Progress Circle */}
        <div className="my-2">
          <CircularProgress
            percentage={percentage}
            color={getIconColor(color)}
            size={90}
          />
        </div>

        {/* Description */}
        <p className="text-xs text-gray-600 leading-relaxed mb-3 flex-grow">
          {description}
        </p>

        {/* Status Badge */}
        <div
          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(
            percentage
          )}`}
        >
          {getStatusText(percentage)}
        </div>
      </div>
    </div>
  );
};

// ✅ Main Dashboard
const VesselSustainabilityDashboard = ({ stockData }) => {
  const [perSustainability, setPerSustainability] = useState(0);
  const [perEnvironment, setPerEnvironment] = useState(0);
  const [perILO, setPerILO] = useState(0);
  const [perSafetyPersonnel, setPerSafetyPersonnel] = useState(0);
  const [perSafetyCraft, setPerSafetyCraft] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tripNumber = stockData?.Trip_Number;
        if (!tripNumber) return;

        const [r1, r2, r3, r4, r5] = await Promise.all([
          checkSustainability(tripNumber),
          checkEnvironment(tripNumber),
          checkILO(tripNumber),
          checkSafetyp(tripNumber),
          checkSafetyc(tripNumber),
        ]);

        const calc = (data) =>
          (
            (data.checklist.filter(Boolean).length / data.checklist.length) *
            100
          ).toFixed(2);

        setPerSustainability(calc(r1.data[0]));
        setPerEnvironment(calc(r2.data[0]));
        setPerILO(calc(r3.data[0]));
        setPerSafetyPersonnel(calc(r4.data[0]));
        setPerSafetyCraft(calc(r5.data[0]));
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    };
    fetchData();
  }, [stockData]);

  const metrics = [
    {
      title: 'Sustainability',
      percentage: perSustainability,
      icon: Leaf,
      color: 'green',
      description:
        'Environmental impact reduction, fuel efficiency, and sustainable practices.',
    },
    {
      title: 'Environment',
      percentage: perEnvironment,
      icon: Shield,
      color: 'blue',
      description:
        'Emissions control, waste management, and environmental compliance.',
    },
    {
      title: 'ILO C188',
      percentage: perILO,
      icon: Users,
      color: 'purple',
      description:
        'ILO Work in Fishing Convention compliance and fair working conditions.',
    },
    {
      title: 'Personnel Safety',
      percentage: perSafetyPersonnel,
      icon: HardHat,
      color: 'orange',
      description:
        'Crew safety protocols, training programs, and personnel welfare measures.',
    },
    {
      title: 'Craft Safety',
      percentage: perSafetyCraft,
      icon: Wrench,
      color: 'red',
      description:
        'Vessel maintenance, equipment integrity, and operational safety systems.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 p-3 p-md-5">
      <Container fluid>
        <Row className="g-3 justify-content-center">
          {metrics.map((metric, i) => (
            <Col
              key={i}
              xs="12"
              sm="6"
              md="4"
              lg="3"
              xl="2"
              className="d-flex justify-content-center"
            >
              <MetricCard {...metric} />
            </Col>
          ))}
        </Row>
      </Container>
    </div>
  );
};

export default VesselSustainabilityDashboard;
